import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, User, Mail, Phone, Lock, Camera, Calendar, Clock, Trash2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface Profile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  member_type: 'consulente' | 'filho' | null;
  member_function: string | null;
  custom_function: string | null;
  pai_de_cabeca: string | null;
  mae_de_cabeca: string | null;
}

interface Appointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  notes: string | null;
}

const Profile = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [paiDeCabeca, setPaiDeCabeca] = useState("");
  const [maeDeCabeca, setMaeDeCabeca] = useState("");
  const [isSavingSpiritual, setIsSavingSpiritual] = useState(false);

  useEffect(() => {
    checkAuthAndLoadProfile();
  }, []);

  const checkAuthAndLoadProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      // Load profile
      let { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      // If profile doesn't exist, create one
      if (profileError && profileError.code === 'PGRST116') {
        const { data: newProfile, error: createError } = await supabase
          .from("profiles")
          .insert({
            id: session.user.id,
            full_name: session.user.user_metadata?.full_name || "",
            email: session.user.email || "",
            phone: session.user.user_metadata?.phone || "",
          })
          .select()
          .single();

        if (createError) throw createError;
        profileData = newProfile;
      } else if (profileError) {
        throw profileError;
      }

      setProfile(profileData);
      setFullName(profileData.full_name || "");
      setEmail(profileData.email || "");
      setPhone(profileData.phone || "");
      setPaiDeCabeca(profileData.pai_de_cabeca || "");
      setMaeDeCabeca(profileData.mae_de_cabeca || "");

      // Load appointments history
      const { data: appointmentsData } = await supabase
        .from("appointments")
        .select("*")
        .eq("user_id", session.user.id)
        .order("appointment_date", { ascending: false });

      setAppointments(appointmentsData || []);
    } catch (error) {
      console.error("Error loading profile:", error);
      toast({
        title: t('profile.error'),
        description: t('profile.loadError'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;

    setIsUploadingAvatar(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Delete old avatar if exists
      if (profile.avatar_url) {
        const oldPath = profile.avatar_url.split("/").pop();
        if (oldPath) {
          await supabase.storage
            .from("avatars")
            .remove([`${user.id}/${oldPath}`]);
        }
      }

      // Upload new avatar
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setProfile({ ...profile, avatar_url: publicUrl });

      // Dispatch event to update avatar in Navbar
      window.dispatchEvent(new CustomEvent('avatar-updated', { detail: { avatarUrl: publicUrl } }));

      toast({
        title: t('profile.success'),
        description: t('profile.avatarUpdated'),
      });
    } catch (error: any) {
      toast({
        title: t('profile.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          phone: phone,
        })
        .eq("id", profile.id);

      if (error) throw error;

      setProfile({ ...profile, full_name: fullName, phone: phone });

      toast({
        title: t('profile.success'),
        description: t('profile.profileUpdated'),
      });
    } catch (error: any) {
      toast({
        title: t('profile.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      toast({
        title: t('profile.error'),
        description: t('auth.passwordMismatch'),
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: t('profile.error'),
        description: t('auth.passwordTooShort'),
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");

      toast({
        title: t('profile.success'),
        description: t('profile.passwordUpdated'),
      });
    } catch (error: any) {
      toast({
        title: t('profile.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Note: Full account deletion would need an edge function with admin privileges
    // For now, we'll sign out and inform the user
    await supabase.auth.signOut();
    toast({
      title: t('profile.accountDeleted'),
      description: t('profile.accountDeletedDesc'),
    });
    navigate("/");
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      confirmed: "default",
      pending: "secondary",
      cancelled: "destructive",
      completed: "outline",
    };
    return <Badge variant={variants[status] || "default"}>{status}</Badge>;
  };

  const handleSaveSpiritualInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setIsSavingSpiritual(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          pai_de_cabeca: paiDeCabeca,
          mae_de_cabeca: maeDeCabeca,
        })
        .eq("id", profile.id);

      if (error) throw error;

      setProfile({ ...profile, pai_de_cabeca: paiDeCabeca, mae_de_cabeca: maeDeCabeca });

      toast({
        title: t('profile.success'),
        description: t('profile.spiritualInfoUpdated'),
      });
    } catch (error: any) {
      toast({
        title: t('profile.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingSpiritual(false);
    }
  };

  const getMemberFunctionLabel = (func: string | null) => {
    if (!func) return "-";
    const labels: Record<string, string> = {
      cambone: t('profile.cambone'),
      curimba: t('profile.curimba'),
      medium_atendimento: t('profile.mediumAtendimento'),
      medium_passe: t('profile.mediumPasse'),
      medium_firmado: t('profile.mediumFirmado'),
    };
    return labels[func] || func;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p>{t('profile.notFound')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title={t('profile.seoTitle')}
        description={t('profile.seoDescription')}
      />
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-16 sm:py-20 px-2 sm:px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl text-primary font-bold text-center mb-6 sm:mb-8">
            {t('profile.title')}
          </h1>

          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            {/* Avatar and Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('profile.avatar')}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4">
                <div className="relative">
                  <Avatar className="w-32 h-32">
                    <AvatarImage src={profile.avatar_url || undefined} />
                    <AvatarFallback className="text-4xl bg-primary/10">
                      <User className="w-16 h-16 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                  <label
                    htmlFor="avatar-upload"
                    className="absolute bottom-0 right-0 p-2 bg-primary text-primary-foreground rounded-full cursor-pointer hover:bg-primary/90 transition-colors"
                  >
                    {isUploadingAvatar ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5" />
                    )}
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={isUploadingAvatar}
                  />
                </div>
                <p className="text-sm text-muted-foreground">{t('profile.uploadPhoto')}</p>
              </CardContent>
            </Card>

            {/* Profile Form */}
            <Card>
              <CardHeader>
                <CardTitle>{t('profile.personalInfo')}</CardTitle>
                <CardDescription>{t('profile.updateInfo')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {t('profile.name')}
                    </Label>
                    <Input
                      id="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('profile.email')}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">{t('profile.emailNote')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {t('profile.phone')}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSaving}>
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {t('profile.save')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Lock className="w-5 h-5" />
                  {t('profile.changePassword')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">{t('profile.newPassword')}</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-new-password">{t('profile.confirmNewPassword')}</Label>
                      <Input
                        id="confirm-new-password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={isSaving} className="w-full sm:w-auto">
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {t('profile.updatePassword')}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Spiritual Info - Only for "Filho" */}
            {profile.member_type === 'filho' && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    ✨ {t('profile.spiritualInfo')}
                  </CardTitle>
                  <CardDescription>{t('profile.spiritualInfoDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  {/* Read-only info from Admin */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">{t('profile.memberType')}</p>
                      <p className="font-medium text-sm sm:text-base">{t('profile.filho')}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">{t('profile.memberFunction')}</p>
                      <p className="font-medium text-sm sm:text-base">
                        {profile.custom_function || getMemberFunctionLabel(profile.member_function)}
                      </p>
                    </div>
                  </div>

                  {/* Editable fields */}
                  <form onSubmit={handleSaveSpiritualInfo} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pai-de-cabeca">{t('profile.paiDeCabeca')}</Label>
                        <Input
                          id="pai-de-cabeca"
                          value={paiDeCabeca}
                          onChange={(e) => setPaiDeCabeca(e.target.value)}
                          placeholder={t('profile.paiDeCabecaPlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mae-de-cabeca">{t('profile.maeDeCabeca')}</Label>
                        <Input
                          id="mae-de-cabeca"
                          value={maeDeCabeca}
                          onChange={(e) => setMaeDeCabeca(e.target.value)}
                          placeholder={t('profile.maeDeCabecaPlaceholder')}
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={isSavingSpiritual} className="w-full sm:w-auto">
                      {isSavingSpiritual ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {t('profile.saveSpiritualInfo')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Appointment History */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Calendar className="w-5 h-5" />
                  {t('profile.appointmentHistory')}
                </CardTitle>
                <CardDescription>{t('profile.appointmentHistoryDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-6 sm:py-8 text-sm sm:text-base">
                    {t('profile.noAppointments')}
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs sm:text-sm">{t('profile.date')}</TableHead>
                          <TableHead className="text-xs sm:text-sm">{t('profile.time')}</TableHead>
                          <TableHead className="text-xs sm:text-sm">{t('profile.status')}</TableHead>
                          <TableHead className="hidden sm:table-cell text-xs sm:text-sm">{t('profile.notes')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {appointments.map((apt) => (
                          <TableRow key={apt.id}>
                            <TableCell className="text-xs sm:text-sm">
                              {format(new Date(apt.appointment_date), "dd/MM/yy", { locale: ptBR })}
                            </TableCell>
                            <TableCell className="text-xs sm:text-sm">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                {apt.appointment_time.slice(0, 5)}
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(apt.status)}</TableCell>
                            <TableCell className="max-w-xs truncate hidden sm:table-cell text-xs sm:text-sm">
                              {apt.notes || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Delete Account */}
            <Card className="md:col-span-2 border-destructive/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive text-lg sm:text-xl">
                  <Trash2 className="w-5 h-5" />
                  {t('profile.dangerZone')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full sm:w-auto">
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('profile.deleteAccount')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('profile.deleteConfirmTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('profile.deleteConfirmDesc')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                      <AlertDialogCancel className="w-full sm:w-auto">{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto"
                      >
                        {t('profile.confirmDelete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;